// ./src/utils/inviteRows.ts
//
// The review list of the "add guests" drawer (`PasteRecipientsModal`) — one row per invitation, pasted or picked from
// the address book — and what the import is sent for each (ROADMAP A2b #62 P6c; the server half is P2b,
// `EventGuestCore.importGuests` + `inviteHousehold.ts` in eventsmagic-api).
//
// Every row has a GROUP SIZE (1 = just them, 2 = a couple, 5 = a family) and, from 2 up, a GROUP NAME — who the
// invitation is addressed to ("John & Anna"). A named group of 2+ becomes a household in the address book, server-side.
//
// #62 P6c Phase B — FAMILIES: ONE INVITATION, SEVERAL LINKS. A family picked from the address book is ONE row (the lead,
// carrying the family's size); the other adults the host ticks ride along as `alsoNotifyPersonIds` — each gets their own
// link to that same invitation. Kids come along in the size; only an adult with an email or phone can be ticked.
//
// ⚠ Pure and TYPE-ONLY in its imports, so `npm run check-event-setup` can run it under Node.
import type { ImportGuestRecipient } from './api/EventGuestService';
import type { Person } from './api/PersonService';

export interface InviteRow extends ImportGuestRecipient {
  key: string;
  /** #62 P6c Phase B — the ticked adults riding along, by name, for the review. */
  alsoNotifyNames?: string[];
  /** A picked contact's name — the row is theirs, so it needs no label until it is a group. */
  contactName?: string | null;
  /** A picked contact's email or phone, for the row's second line. */
  contactLine?: string | null;
}

/** A contact picked from the address book, waiting for the review step. */
export interface PickedContact {
  personId: string;
  name: string;
  /** Lower-cased — a pasted line with the same address is the same person. */
  email: string | null;
  /** As stored (E.164) — a pasted line with the same number is the same person too. */
  phone: string | null;
  contactLine: string | null;
  /** Their household's name, when they are in one — what a group of 2+ starts out addressed to. */
  householdName: string | null;
  /** #62 P6c Phase B — a picked FAMILY: its other ticked adults (their own links to this invitation). */
  alsoNotify?: Array<{ personId: string; name: string; email?: string | null; phone?: string | null }>;
  /** #62 P6c Phase B — a picked family's size (everyone in the household, kids included) — the row starts at it. */
  familySize?: number | null;
  /** #62 P6c Phase B — the household this pick came from, so the same family cannot be picked twice. */
  householdId?: string | null;
}

/** The group size a row stands for (an emptied box counts as 1). */
export const inviteRowSize = (row: Pick<InviteRow, 'rsvpGroupSize'>): number =>
  typeof row.rsvpGroupSize === 'number' && row.rsvpGroupSize > 0 ? row.rsvpGroupSize : 1;

/** Does this row show a name box? A pasted row always (its label is all we know); a picked contact only as a group. */
export const inviteRowNeedsName = (row: InviteRow): boolean => !row.personId || inviteRowSize(row) > 1;

/**
 * The review list: the picked contacts first, then the pasted lines — minus a pasted line that IS a picked contact, by
 * email OR phone. ⚠ The server matches both rows to the same person and the second one silently overwrites the group
 * size and name the host chose on the first.
 */
export const buildInviteRows = (picked: readonly PickedContact[], pasted: readonly ImportGuestRecipient[], size: number): InviteRow[] => {
  // ⚠ Phase B — the adults riding along with a picked family are picked too: a pasted line with one of THEIR addresses
  //   is the same person, and would otherwise become a second row for someone the family's invitation already reaches.
  const everyone = picked.flatMap((p) => [{ email: p.email, phone: p.phone }, ...(p.alsoNotify ?? [])]);
  const pickedEmails = new Set(everyone.map((p) => p.email?.toLowerCase()).filter((e): e is string => !!e));
  const pickedPhones = new Set(everyone.map((p) => p.phone).filter((e): e is string => !!e));

  const contactRows: InviteRow[] = picked.map((p) => ({
    key: `person-${p.personId}`,
    personId: p.personId,
    contactName: p.name,
    contactLine: p.contactLine,
    groupName: p.householdName,
    // A picked FAMILY starts at its own size — the people the host can see in it — never the event's default.
    rsvpGroupSize: p.familySize && p.familySize > 1 ? p.familySize : size,
    alsoNotifyPersonIds: (p.alsoNotify ?? []).map((a) => a.personId),
    alsoNotifyNames: (p.alsoNotify ?? []).map((a) => a.name),
  }));
  const pasteRows: InviteRow[] = pasted
    .filter((r) => !(r.email && pickedEmails.has(r.email.toLowerCase())) && !(r.phone && pickedPhones.has(r.phone)))
    .map((r, i) => ({ ...r, rsvpGroupSize: size, key: `${r.email || r.phone || 'row'}-${i}` }));

  return [...contactRows, ...pasteRows];
};

/** Who a review row is for — a picked contact, else the pasted address — independent of its position in the paste. */
export const inviteRowIdentity = (r: InviteRow): string =>
  r.personId ? `person:${r.personId}` : `${r.email || r.phone || r.key}`.toLowerCase();

/**
 * The rows a fresh Review shows after Back → Review again (#124 review). The paste is re-read, so `built` is the truth
 * for who is on the list; a size or a name the host already SET on the review screen (it differs from what the last
 * Review built) stays with its person, and everything untouched takes the fresh value (a new default size, an edited
 * label in the paste). Matched by contact, not position: a pasted row's key carries its line number.
 */
export const carryReviewEdits = (built: InviteRow[], builtBefore: readonly InviteRow[], reviewed: readonly InviteRow[]): InviteRow[] => {
  const before = new Map(builtBefore.map((r) => [inviteRowIdentity(r), r]));
  const now = new Map(reviewed.map((r) => [inviteRowIdentity(r), r]));

  return built.map((row) => {
    const was = before.get(inviteRowIdentity(row));
    const edited = now.get(inviteRowIdentity(row));
    if (!was || !edited) {
      return row;
    }

    // The two things the host can change on a review row.
    const changed = (field: 'rsvpGroupSize' | 'groupName') => (edited[field] ?? null) !== (was[field] ?? null);

    return {
      ...row,
      rsvpGroupSize: changed('rsvpGroupSize') ? edited.rsvpGroupSize : row.rsvpGroupSize,
      groupName: changed('groupName') ? edited.groupName : row.groupName,
    };
  });
};

/**
 * What the import is sent for one reviewed row.
 *
 * ⚠ A picked contact on their own sends NO group name: they ARE the name on the invitation, and a label would create a
 * one-person group named after a box that is hidden at size 1 (it still holds their household's name). From 2 up the
 * name is the group's — and with it the server sets up the household.
 */
export const toImportRecipient = (row: InviteRow): ImportGuestRecipient => {
  const size = typeof row.rsvpGroupSize === 'number' ? row.rsvpGroupSize : null;

  const alsoNotify = (row.alsoNotifyPersonIds ?? []).filter(Boolean);

  return {
    personId: row.personId || null,
    email: row.email || null,
    phone: row.phone || null,
    groupName: inviteRowNeedsName(row) ? row.groupName?.trim() || null : null,
    rsvpGroupSize: size,
    // ⚠ Only when there ARE some: an unknown input field fails the whole import on a gateway not yet recomposed.
    ...(alsoNotify.length > 0 ? { alsoNotifyPersonIds: alsoNotify } : {}),
  };
};

/**
 * Is this row somebody ALREADY on the guest list? Adding them again UPDATES their group size and name (the import
 * matches them), so the review says so rather than letting a re-pasted list quietly reset earlier choices.
 */
export const inviteRowIsExisting = (
  row: InviteRow,
  existing: { personIds: ReadonlySet<string>; emails: ReadonlySet<string>; phones: ReadonlySet<string> },
): boolean =>
  (!!row.personId && existing.personIds.has(row.personId)) ||
  (!!row.email && existing.emails.has(row.email.toLowerCase())) ||
  (!!row.phone && existing.phones.has(row.phone));

// ─── #62 P6c Phase B — the family picker ─────────────────────────────────────────────────────────────────────────

/** The age a household member counts as an adult from, when the event declares none (mirrors the API's default). */
export const FAMILY_AGE_OF_MAJORITY = 18;

type PersonLike = Pick<
  Person,
  'id' | 'firstName' | 'lastName' | 'email' | 'phone' | 'personGroupId' | 'personGroupRole' | 'birthdayYear' | 'birthdayMonth' | 'birthdayDay'
>;

export interface FamilyMember {
  personId: string;
  name: string;
  email: string | null;
  phone: string | null;
  isMinor: boolean;
  /** An adult with an email or phone — the only kind who can be sent an invitation. */
  canReceive: boolean;
  isPrimary: boolean;
}

export interface FamilyOption {
  /** `household-<id>`, or `person-<id>` for someone in no household. */
  key: string;
  householdId: string | null;
  name: string;
  members: FamilyMember[];
}

/**
 * Is this household member a child? The address book says so (`minor`), else a birthday WITH a year says under age —
 * an unknown age counts as an adult, the API's own rule (`isAnswerRequiredFor`). ⚠ A birthday stored without its year
 * (placeholder 2000) proves nothing about age.
 */
export const memberIsMinor = (p: PersonLike, asOf: Date, ageOfMajority: number = FAMILY_AGE_OF_MAJORITY): boolean => {
  if (p.personGroupRole === 'minor') {
    return true;
  }

  if (!p.birthdayYear) {
    return false;
  }

  let age = asOf.getFullYear() - p.birthdayYear;
  const month = p.birthdayMonth ?? 1;
  const day = p.birthdayDay ?? 1;

  if (asOf.getMonth() + 1 < month || (asOf.getMonth() + 1 === month && asOf.getDate() < day)) {
    age -= 1;
  }

  return age < ageOfMajority;
};

const personName = (p: PersonLike): string => [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || p.email || p.phone || 'Unnamed';

const toMember = (p: PersonLike, asOf: Date, ageOfMajority: number): FamilyMember => {
  const isMinor = memberIsMinor(p, asOf, ageOfMajority);
  const email = p.email?.trim() ? p.email.trim().toLowerCase() : null;
  const phone = p.phone?.trim() || null;

  return {
    personId: String(p.id),
    name: personName(p),
    email,
    phone,
    isMinor,
    canReceive: !isMinor && !!(email || phone),
    isPrimary: p.personGroupRole === 'primary',
  };
};

/**
 * The picker's results, grouped: each HOUSEHOLD once (with ALL its members, found or not), then each person in no
 * household. Order follows the search results. Members: the primary first, then adults, then children.
 */
export const buildFamilyOptions = (
  found: readonly PersonLike[],
  householdMembers: readonly PersonLike[],
  householdNames: Readonly<Record<string, string | null | undefined>>,
  asOf: Date,
  ageOfMajority: number = FAMILY_AGE_OF_MAJORITY,
): FamilyOption[] => {
  const out: FamilyOption[] = [];
  const seen = new Set<string>();
  const rank = (m: FamilyMember): number => (m.isPrimary ? 0 : m.isMinor ? 2 : 1);

  for (const p of found) {
    if (!p.id) {
      continue;
    }

    const hid = p.personGroupId ? String(p.personGroupId) : null;
    const key = hid ? `household-${hid}` : `person-${p.id}`;

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);

    const people = hid ? householdMembers.filter((m) => String(m.personGroupId) === hid) : [p];
    const all = people.some((m) => String(m.id) === String(p.id)) ? people : [p, ...people];
    const members = all.map((m) => toMember(m, asOf, ageOfMajority)).sort((a, b) => rank(a) - rank(b));

    out.push({ key, householdId: hid, name: (hid && householdNames[hid]?.trim()) || personName(p), members });
  }

  return out;
};

/**
 * Who is ticked when a family first shows: its primary if they can receive, else its first adult who can — never someone
 * already invited (`invited`), or a family whose primary is on the list would open with nobody ticked.
 */
export const defaultReceivers = (family: FamilyOption, invited: ReadonlySet<string> = new Set()): string[] => {
  const open = family.members.filter((m) => m.canReceive && !invited.has(m.personId));
  const pick = open.find((m) => m.isPrimary) ?? open[0];

  return pick ? [pick.personId] : [];
};

/**
 * A picked family as ONE invitation: the LEAD is the ticked adult listed first (the primary when ticked), the other ticked
 * adults ride along with their own links. Null when someone COULD receive it and nobody is ticked (the host unticked them).
 * ⚠ A family where NOBODY has an email or phone is still addable — the list is the list; the invitation just cannot be
 *   sent (the host shares the link another way). Its lead is its first adult.
 * ⚠ A family of one is a plain contact — no household name, no size of its own.
 */
export const familyToPicked = (
  family: FamilyOption,
  receiverIds: readonly string[],
  // ⚠ People who already have an invitation on this event (a guest row, or a link to another one) — never ticked, and
  //   not counted in the family's size: they are counted where they already are.
  invited: ReadonlySet<string> = new Set(),
): PickedContact | null => {
  const ticked = family.members.filter((m) => m.canReceive && !invited.has(m.personId) && receiverIds.includes(m.personId));
  const nobodyCan = !family.members.some((m) => m.canReceive);

  if (ticked.length === 0 && !nobodyCan) {
    return null;
  }

  const fallback = family.members.find((m) => !m.isMinor && !invited.has(m.personId));

  if (!fallback) {
    return null;
  }

  const [lead, ...others] = ticked.length > 0 ? ticked : [fallback];
  const stillToInvite = family.members.filter((m) => !invited.has(m.personId)).length;
  const isHousehold = !!family.householdId && family.members.length > 1;

  return {
    personId: lead.personId,
    name: isHousehold ? family.name : lead.name,
    email: lead.email,
    phone: lead.phone,
    contactLine: lead.email || lead.phone,
    householdName: isHousehold ? family.name : null,
    alsoNotify: others.map((m) => ({ personId: m.personId, name: m.name, email: m.email, phone: m.phone })),
    familySize: isHousehold ? Math.max(1, stillToInvite) : null,
    householdId: family.householdId,
  };
};
